import { createEffect, createSignal, type Accessor } from 'solid-js'

type ReCaptchaApi = {
  ready: (callback: () => void) => void
  execute: (siteKey: string, options: { action: string }) => Promise<string>
}

declare global {
  interface Window {
    grecaptcha?: ReCaptchaApi
  }
}

let loadedSiteKey: string | null = null
let scriptLoadingPromise: Promise<void> | null = null

const loadRecaptchaScript = (siteKey: string) => {
  if (window.grecaptcha && loadedSiteKey === siteKey) {
    return Promise.resolve()
  }

  if (scriptLoadingPromise && loadedSiteKey === siteKey) {
    return scriptLoadingPromise
  }

  const existing = document.querySelector<HTMLScriptElement>('script[data-recaptcha-script="true"]')
  if (existing) {
    existing.remove()
  }

  loadedSiteKey = siteKey
  scriptLoadingPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`
    script.async = true
    script.defer = true
    script.dataset.recaptchaScript = 'true'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA script.'))
    document.head.appendChild(script)
  })

  scriptLoadingPromise.catch(() => {
    scriptLoadingPromise = null
  })

  return scriptLoadingPromise
}

export const useRecaptchaExecution = (enabled: Accessor<boolean>, siteKey: Accessor<string | null>) => {
  const [loadError, setLoadError] = createSignal<string | null>(null)

  createEffect(() => {
    const isEnabled = enabled()
    const key = siteKey()

    if (!isEnabled || !key) {
      return
    }

    loadRecaptchaScript(key).catch(() => {
      setLoadError('Failed to load reCAPTCHA. Please refresh and try again.')
    })
  })

  const execute = async (action: string) => {
    const isEnabled = enabled()
    const key = siteKey()

    if (!isEnabled || !key) {
      return ''
    }

    setLoadError(null)
    await loadRecaptchaScript(key)

    if (!window.grecaptcha) {
      throw new Error('reCAPTCHA failed to initialize.')
    }

    return new Promise<string>((resolve, reject) => {
      window.grecaptcha?.ready(() => {
        window.grecaptcha
          ?.execute(key, { action })
          .then(resolve)
          .catch(() => reject(new Error('Could not complete reCAPTCHA verification.')))
      })
    })
  }

  return {
    execute,
    loadError
  }
}
